/**
 * 记住密码工具
 * 提供安全的密码存储和读取功能
 */

import { encrypt, decrypt } from './crypto'
import { getDeviceFingerprint } from './deviceFingerprint'

const STORAGE_KEY = 'remembered_credentials'
const VERSION = '1.0' // 版本号，用于未来升级兼容性
const EXPIRE_DAYS = 30 // 记住密码的有效期（天）
const EXPIRE_MS = EXPIRE_DAYS * 24 * 60 * 60 * 1000 // 30天的毫秒数

/**
 * 保存记住的密码
 * @param {string} username 用户名
 * @param {string} password 密码（明文）
 * @returns {boolean} 是否保存成功
 */
export function saveRememberedPassword(username, password) {
  try {
    if (!username || !password) {
      console.warn('用户名或密码为空，无法保存')
      return false
    }
    
    const deviceFingerprint = getDeviceFingerprint()
    
    // 加密用户名和密码
    const encryptedUsername = encrypt(username, deviceFingerprint)
    const encryptedPassword = encrypt(password, deviceFingerprint)
    
    // 保存到 localStorage
    const data = {
      version: VERSION,
      username: encryptedUsername,
      password: encryptedPassword,
      timestamp: Date.now(),
      deviceFingerprint: deviceFingerprint // 保存设备指纹，用于验证
    }
    
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
    console.log('密码已安全保存')
    return true
  } catch (error) {
    console.error('保存记住密码失败:', error)
    return false
  }
}

/**
 * 读取记住的密码
 * @returns {Object|null} { username, password } 或 null
 */
export function getRememberedPassword() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (!stored) {
      return null
    }
    
    const data = JSON.parse(stored)
    
    // 检查版本兼容性
    if (!data.version || data.version !== VERSION) {
      console.warn('记住密码版本不兼容，清除旧数据')
      clearRememberedPassword()
      return null
    }
    
    // 检查是否过期（30天）
    if (data.timestamp) {
      const now = Date.now()
      const elapsed = now - data.timestamp
      if (elapsed > EXPIRE_MS) {
        console.warn(`记住的密码已过期（已保存 ${Math.floor(elapsed / (24 * 60 * 60 * 1000))} 天），自动清除`)
        clearRememberedPassword()
        return null
      }
    }
    
    // 验证设备指纹（跨设备/浏览器时无法解密）
    const currentFingerprint = getDeviceFingerprint()
    if (data.deviceFingerprint !== currentFingerprint) {
      console.warn('设备指纹不匹配，可能是跨设备访问，清除记住的密码')
      clearRememberedPassword()
      return null
    }
    
    // 解密用户名和密码
    const deviceFingerprint = data.deviceFingerprint
    const username = decrypt(data.username, deviceFingerprint)
    const password = decrypt(data.password, deviceFingerprint)
    
    return {
      username,
      password,
      timestamp: data.timestamp,
      expireTime: data.timestamp + EXPIRE_MS // 过期时间
    }
  } catch (error) {
    console.error('读取记住密码失败:', error)
    // 如果解密失败（可能是设备指纹不匹配），清除数据
    if (error.message && error.message.includes('解密失败')) {
      clearRememberedPassword()
    }
    return null
  }
}

/**
 * 清除记住的密码
 */
export function clearRememberedPassword() {
  try {
    localStorage.removeItem(STORAGE_KEY)
    console.log('已清除记住的密码')
  } catch (error) {
    console.error('清除记住密码失败:', error)
  }
}

/**
 * 检查是否有记住的密码
 * @returns {boolean}
 */
export function hasRememberedPassword() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (!stored) {
      return false
    }
    
    const data = JSON.parse(stored)
    
    // 检查是否过期
    if (data.timestamp) {
      const now = Date.now()
      const elapsed = now - data.timestamp
      if (elapsed > EXPIRE_MS) {
        // 已过期，清除数据
        clearRememberedPassword()
        return false
      }
    }
    
    const currentFingerprint = getDeviceFingerprint()
    
    // 检查设备指纹是否匹配
    return data.deviceFingerprint === currentFingerprint
  } catch (error) {
    return false
  }
}

/**
 * 获取记住密码的剩余有效天数
 * @returns {number|null} 剩余天数，如果不存在或已过期返回 null
 */
export function getRemainingDays() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (!stored) {
      return null
    }
    
    const data = JSON.parse(stored)
    if (!data.timestamp) {
      return null
    }
    
    const now = Date.now()
    const elapsed = now - data.timestamp
    if (elapsed > EXPIRE_MS) {
      return null // 已过期
    }
    
    const remaining = EXPIRE_MS - elapsed
    const remainingDays = Math.floor(remaining / (24 * 60 * 60 * 1000))
    return remainingDays
  } catch (error) {
    return null
  }
}

